import { useCallback } from 'react';
import { VehicleAttributeCategory } from 'src/entities/VehicleAttributeValue.entity';
import { buildVehicleAttributeOptions } from 'src/utils/vehicleAttributeOptions';

import { useVehicleAttributeValuesRequest } from './useVehicleAttributeValues.request';

/**
 * 車両条件の選択肢を画面へ配る。#329 P8
 *
 * 値は会社ごとに変わるので型（union）では表せず、安全性は API の inclusion 検証が担う。
 * 代わりに**カテゴリ**は API 固定の 7 種なので `VehicleAttributeCategory` で受け、
 * 「車種の選択肢を車両タイプの欄に渡す」取り違えを型で止める。
 *
 * 選択肢一覧のキャッシュは `useVehicleAttributeValuesRequest` をそのまま使う。
 * このキーは改名ジョブの `reset_key` と同じもので、揃っていないと改名の反映後に
 * 古い選択肢が残る（#329 P6）。
 *
 * @param companyId 選択肢を引く会社。**編集するレコード自身の会社**を渡す。
 *   全社表示では一覧に他社のレコードが混ざり、選択中の会社とレコードの会社が食い違う。
 *   省略すると従来どおりログイン会社ぶん（レコードの会社が常に選択中の会社である画面はそれでよい）。
 */
export const useVehicleAttributeOptions = (companyId?: number) => {
  const { data, isError } = useVehicleAttributeValuesRequest(companyId);

  /**
   * @param category 引きたいカテゴリ
   * @param currentValues 編集中のレコードが今持っている値。選択肢に無いものは末尾に残る
   *
   * Why not currentValues を省略可能にする: 渡し忘れると **MultiCheckbox の effect が
   * 既存データの値を黙って落とす**（設計書 §5-4 違反）。省略を型エラーにして、
   * 絞り込みのように本当に不要な場所だけが明示的に [] を渡す形にしてある。
   */
  const optionsOf = useCallback(
    (category: VehicleAttributeCategory, currentValues: readonly (string | null | undefined)[]) => (
      buildVehicleAttributeOptions(data?.[category], currentValues)
    ),
    [data],
  );

  return {
    /**
     * 選択肢をまだ取得中。true のまま空の select を出すと「選択肢が 0 件」に見えるので、
     * 呼び出し側は disabled にするか読み込み中と出す。
     *
     * Why not `!data` だけで判定する: 取得に失敗しても data は undefined のままなので、
     * 画面が永久に「読み込み中...」で止まる（`/trucks` の Select は永久 disabled）。
     * 失敗は isError で区別して、呼び出し側が「取得できなかった」と出せるようにする。
     */
    isLoading: !data && !isError,
    /** 取得に失敗した。選択肢は現在値だけになるので、呼び出し側はその旨を伝える */
    isError,
    optionsOf,
  };
};
